import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    agreeToSMS: false,
    agreeToMarketing: false
  });

  const services = [
    'Web Development',
    'Mobile App Development',
    'Software Development',
    'E-commerce',
    'UI/UX Design',
    'Blockchain',
    '3D Animation',
    'Cloud Solutions',
    'Marketing',
    'Copywriting'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Add form submission logic here
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Get in touch with our team for any inquiries or support
            </p>
          </div>
        </div>
      </section>

      {/* Mobile: Form First, Desktop: Info Cards First */}
      
      {/* Contact Form Section - Shows First on Mobile */}
      <section className="py-8 lg:order-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-6 md:p-12 border border-purple-500/20 shadow-2xl shadow-purple-900/50">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                Our team is always here to assist you.
              </h2>
              <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                Our dedicated team is always available to provide expert guidance and tailored solutions to meet your needs.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="John Doe"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Service Type *
                </label>
                <select
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                >
                  <option value="">Choose a service...</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Message *
                </label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400 resize-none"
                  placeholder="Tell us about your project or inquiry..."
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="agreeToSMS"
                  checked={formData.agreeToSMS}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 bg-gray-700 border-gray-600 rounded focus:ring-2 focus:ring-purple-500"
                />
                <label className="text-xs text-gray-400 leading-relaxed">
                  By checking this box and providing phone number, you agree to receive text messages from Invotage regarding customer support and project updates.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="agreeToMarketing"
                  checked={formData.agreeToMarketing}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 bg-gray-700 border-gray-600 rounded focus:ring-2 focus:ring-purple-500"
                />
                <label className="text-xs text-gray-400 leading-relaxed">
                  I agree to receive marketing messages from Invotage. You can opt out anytime by replying "STOP".
                </label>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 md:py-4 rounded-full transition-all shadow-lg shadow-purple-500/50 flex items-center justify-center gap-2"
              >
                <Send className="h-5 w-5" />
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Info Cards - Shows After Form on Mobile */}
      <section className="py-8 lg:order-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Email */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl md:rounded-3xl p-4 md:p-6 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-900/50 transition-all">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Mail className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-xs md:text-sm font-semibold text-gray-400 uppercase mb-2">Email</h3>
              <a href="mailto:info@invotage.com" className="text-sm md:text-xl font-bold text-white hover:text-purple-400 transition-colors break-all">
                info@invotage.com
              </a>
            </div>

            {/* Address */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl md:rounded-3xl p-4 md:p-6 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-900/50 transition-all">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <MapPin className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-xs md:text-sm font-semibold text-gray-400 uppercase mb-2">Address</h3>
              <p className="text-sm md:text-xl font-bold text-white">
                301 42nd Ave, Meridian, MS, 39307
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-4 md:mb-6">
            We understand that every business has unique challenges, which is why we focus on providing efficient, personalized support that helps you achieve your goals without unnecessary delays.
          </p>
          <p className="text-sm md:text-base text-gray-400">
            Reach out to us today and let's build something exceptional together.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;
